import { useMutation } from '@tanstack/react-query';

import { REPORT_SCHEDULE, type LocationId } from '@brightlocal/shared';

import { apiClient } from 'shared/apiClient';
import type { ErrorReporting, Feature } from 'shared/utils';

import { REPORT_CREATE_STEPS, REPORT_CREATE_TOOLS } from '../consts';
import { buildReportCreateErrorReport, runReportStep } from '../utils';

interface UseGBPAReportMutationOptions {
  feature: Feature;
}

export interface GBPAReportMutationVariables {
  /** The ID of the location to create the GBPA report for */
  locationId: LocationId;
  /** The keywords to create the GBPA report */
  keywords: string[];
  /** The location to create the GBPA report */
  location: { city: string; country: string; state?: string };
}

const DEFAULT_GBPA_SCHEDULE = REPORT_SCHEDULE.ADHOC;

// Maximum number of keywords allowed for a GBP report
const MAX_GBP_KEYWORDS = 5;

const TOOL = REPORT_CREATE_TOOLS.GBPA;
const METHOD = 'apiClient.gbpa.createReport / apiClient.gbpa.runReport';

export const useGBPAReportMutation = ({ feature }: UseGBPAReportMutationOptions) =>
  useMutation({
    mutationFn: async ({ locationId, keywords, location }: GBPAReportMutationVariables) => {
      const { city, state, country } = location;
      const context = { locationId, country };

      const { reportId } = await runReportStep({
        tool: TOOL,
        step: REPORT_CREATE_STEPS.CREATE,
        context,
        request: () =>
          apiClient.gbpa.createReport({
            location_id: locationId,
            schedule: DEFAULT_GBPA_SCHEDULE,
            google_location: [city, state, country].filter(Boolean).join(','),
            search_terms: keywords.slice(0, MAX_GBP_KEYWORDS)
          })
      });

      await runReportStep({
        tool: TOOL,
        step: REPORT_CREATE_STEPS.RUN,
        context,
        request: () => apiClient.gbpa.runReport(reportId)
      });
    },
    meta: {
      errorReporting: {
        feature,
        errorMessage: 'Error creating GBPA report',
        method: METHOD,
        resolve: (error) => buildReportCreateErrorReport({ tool: TOOL, feature, method: METHOD, error })
      } satisfies ErrorReporting
    }
  });
